import ServiceTemplate from './service-page-template/service-page-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';

const heroImageSrc = '/static/images/carpentry/doncaster-storage-cabinet-installation-01.webp';
const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
  // Add any other image paths to exclude here...
];

const carpentryGalleryImages = Object.entries(imagesMetadata)
  .filter(([src]) => src.startsWith('/static/images/carpentry/') && !excludedImages.includes(src))
  .map(([src, meta], index) => ({
    id: index + 1,
    src,
    alt: meta.alt,
    location: meta.location
  }));

export default function Carpentry() {
  const servicesData = {
    title: "Carpentry Services",
    subtitle: "Expert custom carpentry, repairs, and installations with quality craftsmanship across Melbourne.",
    metaTitle: "Carpenter Melbourne | Carpentry Repairs & Installations",
    metaDesc: "Melbourne carpentry for custom shelves, doors, partitions, decks and fences. Book a local handyman for practical carpentry repairs and installations.",
    canonicalPath: "/carpentry",

    solutionsTitle: "Our Carpentry Solutions",
    solutionsIntro: "From custom built-ins to structural repairs, we deliver strong, precise, and beautifully finished carpentry work.",

    solutionsList: [
      { 
        emoji: "📏", 
        heading: "Custom Shelving & Built-ins", 
        text: "Bespoke bookshelves, wardrobes, pantries, and entertainment units designed to fit your space perfectly." 
      },
      { 
        emoji: "🚪", 
        heading: "Door Installation & Repairs", 
        text: "New interior and exterior doors, jamb repairs, hinge adjustments, and alignment fixes." 
      },
      { 
        emoji: "🧱", 
        heading: "Partition Walls & Framing", 
        text: "Professional stud walls to create new rooms, home offices, or divide open-plan spaces." 
      },
      { 
        emoji: "🪚", 
        heading: "Deck, Fence & Timber Repairs", 
        text: "Restoration and repairs of decks, fences, pergolas, and timber structures." 
      }
    ],

    serviceImage: {
      src: heroImageSrc,
      alt: heroMeta?.alt || 'Carpentry Services by Handyman In Melbourne'
    },

    extendedDetails: [
      {
        title: "Precision Joinery & Structural Integrity",
        body: "We focus on strong, properly supported construction. Every job uses correct fixing methods, load-appropriate materials, and professional techniques for long-term durability."
      },
      {
        title: "Custom Design & Perfect Fit",
        body: "Our carpentry is tailored to your home. We take accurate measurements and build solutions that maximise space while complementing your existing architecture."
      },
      {
        title: "Melbourne Timber & Climate Expertise",
        body: "We understand how Melbourne’s weather affects timber. Our repairs and new builds use suitable treatments and materials to resist moisture, warping, and wear."
      },
      {
        title: "Clean, Respectful Work Sites",
        body: "We protect your floors and belongings, control sawdust effectively, and always leave the site clean and tidy after completion."
      }
    ],

    galleryImages: carpentryGalleryImages,

    faqs: [
      {
        question: 'What carpentry services do you offer?',
        answer: 'Door repairs, custom shelving, cabinet fixes, flooring repairs, decking, and general timber work.'
      },
      {
        question: 'Do you fix squeaky floors and stairs?',
        answer: 'Absolutely. This is one of our most common repairs.'
      },
      {
        question: 'Can you build custom storage solutions?',
        answer: 'Yes! We design and build fully custom storage solutions such as wardrobes, bookshelves, under-stair storage, and built-in cabinets. We also modify ready-to-assemble flatpack shelves and units to perfectly fit your space and needs.'
      },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": servicesData.title,
    "serviceType": servicesData.title,
    "description": servicesData.metaDesc,
    "url": `https://handymaninmelbourne.au${servicesData.canonicalPath}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Handyman In Melbourne",
      "url": "https://handymaninmelbourne.au/"
    },
    "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
        ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <ServiceTemplate {...servicesData} />
    </>
  );
}
